import { Fragment, useEffect, useRef, useState } from "react";
import { LayoutDashboard, Users, Sparkles, Settings, LogOut, ChevronLeft, HelpCircle, Search, CalendarDays, Eye, ExternalLink } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/hooks/useAuth";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useCoachPendingTranscriptReviewCount } from "@/hooks/useCoachPendingTranscriptReviewCount";
import { useCoachAnalysisReadyToast } from "@/hooks/useCoachAnalysisReadyToast";
import { CoachBreadcrumbs } from "@/components/coach/CoachBreadcrumbs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { CoachBottomNav } from "@/components/layout/CoachBottomNav";
import { DemoBanner } from "@/components/coach/DemoBanner";
import { CommandPalette } from "@/components/coach/CommandPalette";
import { ProductFeedbackToolbarActions } from "@/components/feedback/ProductFeedbackToolbarActions";
import { CoachNotificationBell } from "@/components/coach/CoachNotificationBell";
import { useCommandPalette } from "@/hooks/useCommandPalette";
import { getAvatarInitials } from "@/utils/avatar";
import { useSubscriptionSync } from "@/hooks/useSubscriptionSync";
import { TrialExperience } from "@/components/trial/TrialExperience";
import {
  COACH_VEERA_PANEL_ID,
  COACH_VEERA_PANEL_WIDTH_STYLE,
  useCoachVeeraPanel,
} from "@/hooks/useCoachVeeraPanel";
import { claimCoachMobileWorkspaceHistory } from "@/lib/coachMobileWorkspaceHistory";


interface NavItem {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: "messages" | "leaders";
}

interface NavSection {
  title: string;
  items: NavItem[];
}

function getCoachTourId(url: string) {
  if (url === "/coach/overview") return "coach-tour-overview";
  if (url === "/coach/leaders") return "coach-tour-leaders";
  if (url === "/coach/meetings") return "coach-tour-meetings";
  if (url === "/coach/hub") return "coach-tour-veera";
  if (url === "/coach/settings") return "coach-tour-settings";
  return undefined;
}

const navSections: NavSection[] = [
  {
    title: "Coach",
    items: [
      { title: "Overview", url: "/coach/overview", icon: LayoutDashboard },
      { title: "Leaders", url: "/coach/leaders", icon: Users, badge: "leaders" },
      { title: "Coach Hub", url: "/coach/hub", icon: Sparkles },
      { title: "Meetings", url: "/coach/meetings", icon: CalendarDays },
      { title: "Settings", url: "/coach/settings", icon: Settings },
    ],
  },
];

export interface CoachLayoutPageWorkspace {
  readonly id: string;
  readonly open: boolean;
  readonly content: React.ReactNode;
  readonly onRequestClose: () => void;
}

interface CoachLayoutProps {
  children: React.ReactNode;
  showBreadcrumbs?: boolean;
  pageWorkspace?: CoachLayoutPageWorkspace;
}

const COACH_SIDEBAR_COLLAPSED_STORAGE_KEY = "coach-sidebar-collapsed";
const COACH_VEERA_PANEL_WIDTH_CLASS = "md:pr-[var(--coach-veera-panel-width)]";
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

function getFocusableElements(container: HTMLElement) {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
    .filter((element) => !element.hasAttribute("inert") && element.getAttribute("aria-hidden") !== "true");
}

function focusIsInOwnedMenuPortal(workspace: HTMLElement, active: Element | null) {
  if (!(active instanceof HTMLElement) || workspace.contains(active)) return false;
  const menu = active.closest<HTMLElement>('[role="menu"][aria-labelledby]');
  const triggerId = menu?.getAttribute("aria-labelledby");
  const trigger = triggerId ? document.getElementById(triggerId) : null;
  return Boolean(trigger && workspace.contains(trigger));
}

function readStoredSidebarCollapsed() {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(COACH_SIDEBAR_COLLAPSED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function collapsedNavLabel(item: NavItem, badgeCount: number) {
  if (badgeCount <= 0) return item.title;
  if (item.badge === "leaders") {
    return `${item.title}, ${badgeCount} ${badgeCount === 1 ? "leader" : "leaders"} needing review`;
  }
  if (item.badge === "messages") {
    return `${item.title}, ${badgeCount} unread ${badgeCount === 1 ? "message" : "messages"}`;
  }
  return item.title;
}

export function CoachLayout({ children, showBreadcrumbs = true, pageWorkspace }: CoachLayoutProps) {
  const [collapsed, setCollapsed] = useState(readStoredSidebarCollapsed);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { leadersWithPendingTranscriptReviewCount } = useCoachPendingTranscriptReviewCount();
  const isMobile = useIsMobile();
  const coachVeeraPanel = useCoachVeeraPanel();
  const coachVeeraOpen = coachVeeraPanel.open;
  const closeCoachVeera = coachVeeraPanel.closePanel;
  const showCoachVeera = coachVeeraPanel.openPanel;
  const pageWorkspaceRef = useRef<HTMLElement | null>(null);
  const pageWorkspaceInvokerRef = useRef<HTMLElement | null>(null);
  const pageWorkspaceWasVisibleRef = useRef(false);
  const pageWorkspaceWasMobileRef = useRef(isMobile);
  const previousPageWorkspaceOpenRef = useRef(false);
  const previousCoachVeeraOpenRef = useRef(coachVeeraOpen);
  const pageWorkspaceCloseRef = useRef(pageWorkspace?.onRequestClose);
  pageWorkspaceCloseRef.current = pageWorkspace?.onRequestClose;
  const { isOpen: isCommandOpen, close: closeCommand, open: openCommand } = useCommandPalette();
  useSubscriptionSync();
  useCoachAnalysisReadyToast(user?.id);

  const userName = user?.user_metadata?.full_name || user?.email?.split("@")[0] || "Coach";
  const userInitials = getAvatarInitials(user?.user_metadata?.full_name, user?.email);
  const suppressMobileHeader =
    location.pathname === "/coach/ai" ||
    location.pathname === "/coach/veera";
  const currentCoachPage = location.pathname.split("/").filter(Boolean)[1] ?? "coach";
  const pageWorkspaceOpen = pageWorkspace?.open === true;
  const rightWorkspaceOpen = coachVeeraOpen || pageWorkspaceOpen;
  const pageWorkspaceVisible = pageWorkspaceOpen && !coachVeeraOpen;
  const effectiveCollapsed = collapsed || rightWorkspaceOpen;

  useEffect(() => {
    const pageWorkspaceJustOpened = pageWorkspaceOpen && !previousPageWorkspaceOpenRef.current;
    const coachVeeraJustOpened = coachVeeraOpen && !previousCoachVeeraOpenRef.current;

    previousPageWorkspaceOpenRef.current = pageWorkspaceOpen;
    previousCoachVeeraOpenRef.current = coachVeeraOpen;

    if (pageWorkspaceJustOpened && coachVeeraOpen) {
      closeCoachVeera();
      return;
    }
    if (coachVeeraJustOpened && pageWorkspaceOpen) {
      pageWorkspaceCloseRef.current?.();
    }
  }, [closeCoachVeera, coachVeeraOpen, pageWorkspaceOpen]);

  useEffect(() => {
    const becameMobile = isMobile && !pageWorkspaceWasMobileRef.current;
    if (pageWorkspaceVisible && !pageWorkspaceWasVisibleRef.current) {
      pageWorkspaceInvokerRef.current = document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    }
    if (pageWorkspaceVisible && isMobile && (!pageWorkspaceWasVisibleRef.current || becameMobile)) {
      requestAnimationFrame(() => {
        const workspace = pageWorkspaceRef.current;
        if (!workspace) return;
        (getFocusableElements(workspace)[0] ?? workspace).focus();
      });
    }
    if (!pageWorkspaceVisible && pageWorkspaceWasVisibleRef.current) {
      const invoker = pageWorkspaceInvokerRef.current;
      const controlledId = invoker?.getAttribute("aria-controls");
      pageWorkspaceInvokerRef.current = null;
      if (!coachVeeraOpen) {
        requestAnimationFrame(() => {
          if (invoker?.isConnected) {
            invoker.focus();
            return;
          }
          if (!controlledId) return;
          const connectedInvoker = Array.from(document.querySelectorAll<HTMLElement>("[aria-controls]"))
            .find((candidate) => (
              candidate.isConnected &&
              candidate.getAttribute("aria-controls") === controlledId &&
              !candidate.closest("[inert]")
            ));
          connectedInvoker?.focus();
        });
      }
    }
    pageWorkspaceWasVisibleRef.current = pageWorkspaceVisible;
    pageWorkspaceWasMobileRef.current = isMobile;
  }, [coachVeeraOpen, isMobile, pageWorkspaceVisible]);

  useEffect(() => {
    if (!pageWorkspaceVisible) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.key === "Escape") {
        event.preventDefault();
        pageWorkspaceCloseRef.current?.();
        return;
      }
      if (!isMobile || event.key !== "Tab") return;
      const workspace = pageWorkspaceRef.current;
      if (!workspace) return;
      if (focusIsInOwnedMenuPortal(workspace, document.activeElement)) return;
      const focusable = getFocusableElements(workspace);
      if (focusable.length === 0) {
        event.preventDefault();
        workspace.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !workspace.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !workspace.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isMobile, pageWorkspaceVisible]);

  useEffect(() => {
    if (!isMobile || !pageWorkspaceVisible) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobile, pageWorkspaceVisible]);

  useEffect(() => {
    if (!isMobile || !pageWorkspaceVisible) return;
    return claimCoachMobileWorkspaceHistory(
      "page-workspace",
      () => pageWorkspaceCloseRef.current?.(),
    );
  }, [isMobile, pageWorkspaceVisible]);

  const openCoachVeera = (
    context: { pageName: string; source: string },
    invoker: HTMLElement,
  ) => {
    if (pageWorkspaceOpen) pageWorkspaceCloseRef.current?.();
    showCoachVeera(context, invoker);
  };

  const setSidebarCollapsed = (nextCollapsed: boolean) => {
    setCollapsed(nextCollapsed);
    try {
      window.localStorage.setItem(COACH_SIDEBAR_COLLAPSED_STORAGE_KEY, String(nextCollapsed));
    } catch {
      // Keep the UI responsive even when storage is unavailable.
    }
  };

  // Helper to get badge count for an item
  const getBadgeCount = (badge?: "messages" | "leaders") => {
    if (badge === "leaders") return leadersWithPendingTranscriptReviewCount;
    return 0;
  };

  // Helper to check if a nav item is active
  const isItemActive = (url: string) => {
    return location.pathname === url ||
      (url === "/coach/leaders" && location.pathname.startsWith("/coach/leaders")) ||
      (url === "/coach/meetings" && (
        location.pathname === "/coach/meetings" ||
        location.pathname === "/coach/calendar" ||
        location.pathname === "/coach/calls"
      )) ||
      (url === "/coach/settings" && location.pathname.startsWith("/coach/settings")) ||
      (url === "/coach/hub" && (
        location.pathname === "/coach/hub" ||
        location.pathname.startsWith("/coach/hub/") ||
        location.pathname.startsWith("/coach/identity") ||
        location.pathname.startsWith("/coach/philosophy") ||
        location.pathname.startsWith("/coach/my-voice")
      ));
  };

  if (isMobile) {
    return (
      <>
        <div
          data-testid="coach-layout-page"
          aria-hidden={rightWorkspaceOpen || undefined}
          {...(rightWorkspaceOpen ? { inert: "" } : {})}
          className={cn(
            "min-h-[100dvh] w-full min-w-0 max-w-full overflow-x-clip bg-background flex flex-col transition-opacity [transition-duration:250ms] ease-out motion-reduce:transition-none",
            rightWorkspaceOpen && "pointer-events-none opacity-0",
          )}
        >
        <DemoBanner />
        {/* Command Palette */}
        <CommandPalette isOpen={isCommandOpen} onClose={closeCommand} />

        {!suppressMobileHeader ? (
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border bg-background">
          <span className="text-base font-semibold text-foreground">Coach VEERA</span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              aria-label="Coach Veera"
              aria-controls={COACH_VEERA_PANEL_ID}
              aria-expanded={coachVeeraOpen}
              onClick={(event) => openCoachVeera(
                { pageName: currentCoachPage, source: "coach_mobile_header" },
                event.currentTarget,
              )}
              className="h-8 w-8 shrink-0 border-violet-200 bg-violet-50 text-violet-950 hover:bg-violet-100"
            >
              <Sparkles className="h-4 w-4" />
            </Button>
            <ProductFeedbackToolbarActions reporterRole="coach" compact />
            {/* Search button for mobile */}
            <Button
              variant="ghost"
              size="icon"
              onClick={openCommand}
              className="h-8 w-8"
            >
              <Search className="h-4 w-4" />
            </Button>
            <CoachNotificationBell />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="relative flex items-center rounded-full border border-border p-1">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                        {userInitials}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-popover">
                <DropdownMenuItem onClick={() => navigate("/coach/meetings")}>
                  <CalendarDays className="h-4 w-4 mr-2" />
                  Meetings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/coach/help")}>
                  <HelpCircle className="h-4 w-4 mr-2" />
                  Help & Support
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => signOut()} className="text-destructive focus:text-destructive">
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        ) : null}
        <TrialExperience role="coach" compact mobileRow />


        <main className="flex-1 w-full min-w-0 max-w-full overflow-x-clip overflow-y-auto pb-20">
          {children}
        </main>

        <CoachBottomNav />
        </div>
        {pageWorkspaceVisible ? (
          <aside
            ref={pageWorkspaceRef}
            id={pageWorkspace.id}
            role="dialog"
            aria-labelledby={`${pageWorkspace.id}-title`}
            aria-modal="true"
            data-coach-workspace-rail="true"
            tabIndex={-1}
            className="fixed inset-0 z-50 h-[100dvh] w-full overflow-auto bg-background"
          >
            {pageWorkspace.content}
          </aside>
        ) : null}
      </>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <DemoBanner />
      {/* Command Palette */}
      <CommandPalette isOpen={isCommandOpen} onClose={closeCommand} />

      <div
        data-testid="coach-layout-workspace"
        style={COACH_VEERA_PANEL_WIDTH_STYLE}
        className={cn(
          "flex flex-1 w-full min-w-0 transition-[padding-right] [transition-duration:250ms] ease-out motion-reduce:transition-none",
          rightWorkspaceOpen && COACH_VEERA_PANEL_WIDTH_CLASS,
        )}
      >
        {/* Left Sidebar - Navigation */}
        <aside
          data-testid="coach-layout-navigation"
          className={cn(
            "sticky top-0 h-screen border-r border-sidebar-border bg-sidebar-background transition-[width] [transition-duration:250ms] ease-out motion-reduce:transition-none flex flex-col",
            effectiveCollapsed ? "w-16" : "w-64"
          )}
        >
          {/* Logo & Collapse */}
          <div className={cn(
            "flex h-16 items-center border-b border-sidebar-border",
            effectiveCollapsed ? "justify-center px-3" : "justify-between px-4",
          )}>
            {!effectiveCollapsed && (
              <span className="text-h3 font-bold text-sidebar-foreground">
                Coach VEERA
              </span>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarCollapsed(!collapsed)}
              disabled={rightWorkspaceOpen}
              className="shrink-0 text-sidebar-foreground hover:bg-sidebar-accent"
              aria-label={rightWorkspaceOpen
                ? "Coach navigation temporarily collapsed"
                : effectiveCollapsed
                  ? "Expand coach navigation"
                  : "Collapse coach navigation"}
              title={coachVeeraOpen
                ? "Close Coach Veera to restore the coach navigation"
                : pageWorkspaceOpen
                  ? undefined
                : effectiveCollapsed
                  ? "Expand coach navigation"
                  : "Collapse coach navigation"}
              aria-expanded={!effectiveCollapsed}
            >
              <ChevronLeft
                className={cn(
                  "h-5 w-5 transition-transform",
                  effectiveCollapsed && "rotate-180"
                )}
              />
            </Button>
          </div>

          {/* Quick Search Button */}
          <div className="px-3 pb-2">
            <button
              onClick={openCommand}
              aria-label={effectiveCollapsed ? "Search coach workspace" : undefined}
              title={effectiveCollapsed ? "Search coach workspace" : undefined}
              className={cn(
                "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-small font-medium transition-colors",
                "border border-sidebar-border bg-sidebar-accent/40 text-sidebar-foreground shadow-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
              )}
            >
              <Search className="h-4 w-4" />
              {!effectiveCollapsed && (
                <>
                  <span className="flex-1 text-left">Search...</span>
                  <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                    <span className="text-xs">⌘</span>K
                  </kbd>
                </>
              )}
            </button>
          </div>

          {/* Navigation - Sectioned like leader layout */}
          <nav className="flex-1 overflow-y-auto p-3 space-y-4">
            {navSections.map((section) => (
              <div key={section.title} className="space-y-1">
                {!effectiveCollapsed && (
                  <p className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {section.title}
                  </p>
                )}
                {section.items.map((item) => {
                  const isActive = isItemActive(item.url);
                  const badgeCount = getBadgeCount(item.badge);
                  const collapsedLabel = collapsedNavLabel(item, badgeCount);

                  return (
                    <Fragment key={item.url}>
                    {item.url === "/coach/hub" && !suppressMobileHeader ? (
                      <Link
                        to="/coach/ai"
                        aria-label={effectiveCollapsed ? "Coach Veera" : undefined}
                        title={effectiveCollapsed ? "Coach Veera" : undefined}
                        className={cn(
                          "relative flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-small font-semibold transition-colors",
                          location.pathname === "/coach/ai" || location.pathname === "/coach/veera"
                            ? "bg-primary text-primary-foreground shadow-sm"
                            : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                        )}
                      >
                        <Sparkles className="h-5 w-5 shrink-0" />
                        {!effectiveCollapsed ? <span>Coach Veera</span> : null}
                      </Link>
                    ) : null}
                    <Link
                      to={item.url}
                      aria-label={effectiveCollapsed ? collapsedLabel : undefined}
                      aria-current={isActive ? "page" : undefined}
                      title={effectiveCollapsed ? collapsedLabel : undefined}
                      data-tour-id={getCoachTourId(item.url)}
                      className={cn(
                        "relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-small font-semibold transition-colors",
                        isActive
                          ? "bg-primary text-primary-foreground shadow-sm"
                          : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                      )}
                    >
                      <div className="relative shrink-0">
                        <item.icon className="h-5 w-5" />
                        {badgeCount > 0 && effectiveCollapsed && (
                          <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-primary" />
                        )}
                      </div>
                      {!effectiveCollapsed && (
                        <div className="flex-1 min-w-0">
                          <div className="min-w-0 text-left">
                            <span className="block break-words leading-tight">{item.title}</span>
                          </div>
                          {badgeCount > 0 && (
                            <div className="mt-1 flex flex-wrap items-center gap-2">
                              {badgeCount > 0 && (
                                <span
                                  className={cn(
                                    "flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10px] font-bold",
                                    isActive ? "bg-primary-foreground text-primary" : "bg-primary text-primary-foreground",
                                  )}
                                >
                                  {badgeCount > 99 ? "99+" : badgeCount}
                                </span>
                              )}
                            </div>
                          )}
                          </div>
                      )}
                    </Link>
                    </Fragment>
                  );
                })}
              </div>
            ))}
          </nav>

          {/* Preview Leader View */}
          <div className="px-3 pb-2">
            <a
              href="/preview/today"
              target="_blank"
              rel="noopener noreferrer"
              aria-label={effectiveCollapsed ? "Preview leader view" : undefined}
              title={effectiveCollapsed ? "Preview leader view" : undefined}
              className={cn(
                "border border-primary/20 bg-primary/[0.06] hover:bg-primary/[0.12] text-primary rounded-lg px-3 py-2.5 w-full flex items-center gap-3 text-sm font-medium transition-colors",
                effectiveCollapsed && "justify-center"
              )}
            >
              <Eye className="h-5 w-5 shrink-0" />
              {!effectiveCollapsed && (
                <>
                  <span className="flex-1 text-left">Preview leader view</span>
                  <ExternalLink className="h-4 w-4 opacity-50 shrink-0" />
                </>
              )}
            </a>
          </div>

          {/* Account Menu - Settings, Help & Support, Logout */}
          <div className="border-t border-sidebar-border p-3 space-y-3">
            <TrialExperience role="coach" compact={effectiveCollapsed} />
            <ProductFeedbackToolbarActions
              reporterRole="coach"
              iconOnly={effectiveCollapsed}
              className={effectiveCollapsed ? "items-center" : undefined}
            />
            <div className={cn(effectiveCollapsed ? "flex justify-center" : undefined)}>
              <CoachNotificationBell iconOnly={effectiveCollapsed} />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  aria-label={effectiveCollapsed ? "Coach account menu" : undefined}
                  title={effectiveCollapsed ? "Coach account menu" : undefined}
                  className={cn(
                    "relative flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-small font-medium transition-colors",
                    "text-sidebar-foreground hover:bg-sidebar-accent"
                  )}
                >
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                  {!effectiveCollapsed && (
                    <div className="flex-1 text-left truncate">
                      <p className="text-sm font-medium text-sidebar-foreground truncate">
                        {userName}
                      </p>
                    </div>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-popover">
                <DropdownMenuItem onClick={() => navigate("/coach/settings")}>
                  <Settings className="h-4 w-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/coach/help")}>
                  <HelpCircle className="h-4 w-4 mr-2" />
                  Help & Support
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => signOut()} className="text-destructive focus:text-destructive">
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </aside>

        {/* Main Content */}
        <div
          data-testid="coach-layout-main"
          className="min-w-0 flex-1 flex flex-col overflow-hidden"
        >

          {/* Content area */}
          <div className="flex-1 flex overflow-hidden">
            <main data-testid="coach-layout-page" className="flex-1 overflow-auto">
              {showBreadcrumbs ? (
                <div className="px-8 py-4 border-b bg-background/50 backdrop-blur-sm sticky top-0 z-10">
                  <CoachBreadcrumbs />
                </div>
              ) : null}
              {children}
            </main>
          </div>
        </div>
      </div>
      {pageWorkspaceVisible ? (
        <aside
          ref={pageWorkspaceRef}
          id={pageWorkspace.id}
          aria-labelledby={`${pageWorkspace.id}-title`}
          data-coach-workspace-rail="true"
          style={COACH_VEERA_PANEL_WIDTH_STYLE}
          className="fixed inset-y-0 right-0 z-50 w-[var(--coach-veera-panel-width)] overflow-auto border-l bg-background shadow-xl"
        >
          {pageWorkspace.content}
        </aside>
      ) : null}
    </div>
  );
}
